import * as THREE from "three";
import { BLOCK_SIZE, COLOR_JITTER_DEFAULT } from "../config";
import { BLOCK, type BlockKind } from "../types";

export interface MaterialEntry {
    material: THREE.MeshStandardMaterial;
    geometry: THREE.BufferGeometry;
    jitter: number; // 0..1; per-instance color multiplier amplitude
    /** Cell boundaries fully covered by the geometry, used for shared-face culling. */
    boundaryFaces: number;
}

export type MaterialMap = Record<BlockKind, MaterialEntry>;

export const BLOCK_FACE = {
    positiveX: 1 << 0,
    negativeX: 1 << 1,
    positiveY: 1 << 2,
    negativeY: 1 << 3,
    positiveZ: 1 << 4,
    negativeZ: 1 << 5,
} as const;

export const ALL_BLOCK_FACES = (1 << 6) - 1;

const LOWERED_BLOCK_BOUNDARY_FACES =
    BLOCK_FACE.positiveX |
    BLOCK_FACE.negativeX |
    BLOCK_FACE.negativeY |
    BLOCK_FACE.positiveZ |
    BLOCK_FACE.negativeZ;

function cube() {
    return new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_SIZE);
}

function flatPad() {
    const g = new THREE.BoxGeometry(BLOCK_SIZE * 0.9, BLOCK_SIZE * 0.1, BLOCK_SIZE * 0.9);
    g.translate(0, -BLOCK_SIZE * 0.6, 0);
    return g;
}

function waterGeometry() {
    const g = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE * 0.85, BLOCK_SIZE);
    g.translate(0, -BLOCK_SIZE * 0.075, 0);
    return g;
}

function flowerGeometry() {
    return new THREE.BoxGeometry(BLOCK_SIZE * 0.25, BLOCK_SIZE * 0.55, BLOCK_SIZE * 0.25);
}

function mushroomGeometry() {
    return new THREE.BoxGeometry(BLOCK_SIZE * 0.4, BLOCK_SIZE * 0.6, BLOCK_SIZE * 0.4);
}

function cactusGeometry() {
    return new THREE.BoxGeometry(BLOCK_SIZE * 0.55, BLOCK_SIZE, BLOCK_SIZE * 0.55);
}

function rockGeometry() {
    return new THREE.BoxGeometry(BLOCK_SIZE * 0.7, BLOCK_SIZE * 0.55, BLOCK_SIZE * 0.7);
}

function tallGrassGeometry() {
    return new THREE.BoxGeometry(BLOCK_SIZE * 0.45, BLOCK_SIZE * 0.7, BLOCK_SIZE * 0.45);
}

function deadBushGeometry() {
    return new THREE.BoxGeometry(BLOCK_SIZE * 0.4, BLOCK_SIZE * 0.55, BLOCK_SIZE * 0.4);
}

function iceSpikeGeometry() {
    return new THREE.ConeGeometry(BLOCK_SIZE * 0.45, BLOCK_SIZE, 6);
}

export function createMaterials(): {
    map: MaterialMap;
    /** Geometries owned by this map. SceneManager disposes them once. */
    geometries: THREE.BufferGeometry[];
} {
    const cubeGeom = cube();

    const m = (
        material: THREE.MeshStandardMaterial,
        geometry: THREE.BufferGeometry,
        jitter: number = COLOR_JITTER_DEFAULT,
        boundaryFaces: number = geometry === cubeGeom ? ALL_BLOCK_FACES : 0,
    ): MaterialEntry => ({ material, geometry, jitter, boundaryFaces });

    // Mid-saturation palette: enough chroma to read biomes at a glance,
    // toned down vs Minecraft-vivid so it integrates with the site palette.
    const map: MaterialMap = {
        [BLOCK.Bedrock]: m(new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 1 }), cubeGeom, 0),
        [BLOCK.Stone]: m(new THREE.MeshStandardMaterial({ color: 0x8e8e8e, roughness: 1 }), cubeGeom, 0.05),
        [BLOCK.Dirt]: m(new THREE.MeshStandardMaterial({ color: 0x7a5e3c, roughness: 1 }), cubeGeom),
        [BLOCK.Grass]: m(new THREE.MeshStandardMaterial({ color: 0x6e9560, roughness: 0.9 }), cubeGeom, 0.06),
        [BLOCK.Sand]: m(new THREE.MeshStandardMaterial({ color: 0xd6b97a, roughness: 1 }), cubeGeom, 0.04),
        [BLOCK.Sandstone]: m(new THREE.MeshStandardMaterial({ color: 0xc09870, roughness: 1 }), cubeGeom, 0.03),
        [BLOCK.Log]: m(new THREE.MeshStandardMaterial({ color: 0x6a4520, roughness: 1 }), cubeGeom, 0.03),
        [BLOCK.Leaves]: m(
            new THREE.MeshStandardMaterial({
                color: 0x5cab3f,
                transparent: true,
                opacity: 0.9,
                roughness: 1,
                depthWrite: false,
            }),
            cubeGeom,
            0.08,
        ),
        [BLOCK.PineLeaves]: m(
            new THREE.MeshStandardMaterial({
                color: 0x365e3c,
                transparent: true,
                opacity: 0.92,
                roughness: 1,
                depthWrite: false,
            }),
            cubeGeom,
            0.06,
        ),
        [BLOCK.Water]: m(
            new THREE.MeshStandardMaterial({
                color: 0x4a82b8,
                transparent: true,
                opacity: 0.8,
                roughness: 0.3,
                blending: THREE.NormalBlending,
            }),
            waterGeometry(),
            0,
            LOWERED_BLOCK_BOUNDARY_FACES,
        ),
        [BLOCK.Ice]: m(
            new THREE.MeshStandardMaterial({
                color: 0xb5dde8,
                transparent: true,
                opacity: 0.9,
                roughness: 0.2,
                blending: THREE.NormalBlending,
            }),
            cubeGeom,
            0,
        ),
        [BLOCK.SnowBlock]: m(new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.8 }), cubeGeom, 0),
        [BLOCK.Cactus]: m(new THREE.MeshStandardMaterial({ color: 0x5d8246, roughness: 1 }), cactusGeometry(), 0.05),
        [BLOCK.FlowerRed]: m(new THREE.MeshStandardMaterial({ color: 0xc25555, roughness: 1 }), flowerGeometry(), 0.1),
        [BLOCK.FlowerYellow]: m(new THREE.MeshStandardMaterial({ color: 0xdcb958, roughness: 1 }), flowerGeometry(), 0.1),
        [BLOCK.Mushroom]: m(new THREE.MeshStandardMaterial({ color: 0xa05858, roughness: 1 }), mushroomGeometry(), 0.08),
        [BLOCK.LilyPad]: m(new THREE.MeshStandardMaterial({ color: 0x428052, roughness: 1 }), flatPad(), 0.06),
        [BLOCK.IceSpike]: m(
            new THREE.MeshStandardMaterial({
                color: 0xc8e5ee,
                transparent: true,
                opacity: 0.9,
                roughness: 0.3,
            }),
            iceSpikeGeometry(),
            0,
        ),
        [BLOCK.Rock]: m(new THREE.MeshStandardMaterial({ color: 0x787878, roughness: 1 }), rockGeometry(), 0.06),
        [BLOCK.TallGrass]: m(
            new THREE.MeshStandardMaterial({ color: 0x82a256, roughness: 1, transparent: true, opacity: 0.95, depthWrite: false }),
            tallGrassGeometry(),
            0.1,
        ),
        [BLOCK.DeadBush]: m(
            new THREE.MeshStandardMaterial({ color: 0x8a6c44, roughness: 1, transparent: true, opacity: 0.95, depthWrite: false }),
            deadBushGeometry(),
            0.08,
        ),
        [BLOCK.CoarseDirt]: m(new THREE.MeshStandardMaterial({ color: 0x665038, roughness: 1 }), cubeGeom, 0.07),
        [BLOCK.Terracotta]: m(new THREE.MeshStandardMaterial({ color: 0xb5754a, roughness: 1 }), cubeGeom, 0.04),
        [BLOCK.RedTerracotta]: m(new THREE.MeshStandardMaterial({ color: 0xa04c34, roughness: 1 }), cubeGeom, 0.04),
        [BLOCK.WhiteTerracotta]: m(new THREE.MeshStandardMaterial({ color: 0xd9c5ab, roughness: 1 }), cubeGeom, 0.03),
        [BLOCK.Mycelium]: m(new THREE.MeshStandardMaterial({ color: 0x8d7a8a, roughness: 1 }), cubeGeom, 0.06),
        [BLOCK.MushroomStem]: m(new THREE.MeshStandardMaterial({ color: 0xd8d1c4, roughness: 1 }), cubeGeom, 0.03),
        [BLOCK.MushroomCapRed]: m(new THREE.MeshStandardMaterial({ color: 0xc24b4b, roughness: 0.9 }), cubeGeom, 0.05),
        [BLOCK.MushroomCapBrown]: m(new THREE.MeshStandardMaterial({ color: 0xa87d5c, roughness: 0.9 }), cubeGeom, 0.05),
        [BLOCK.Netherrack]: m(new THREE.MeshStandardMaterial({ color: 0x9a4038, roughness: 1 }), cubeGeom, 0.09),
        [BLOCK.Lava]: m(
            new THREE.MeshStandardMaterial({
                color: 0xff8c3a,
                emissive: 0xff4a00,
                emissiveIntensity: 0.6,
                roughness: 1,
            }),
            waterGeometry(),
            0,
            LOWERED_BLOCK_BOUNDARY_FACES,
        ),
        [BLOCK.Glowstone]: m(
            new THREE.MeshStandardMaterial({
                color: 0xc9a15a,
                emissive: 0xffc46a,
                emissiveIntensity: 0.7,
                roughness: 0.8,
            }),
            cubeGeom,
            0,
        ),
        [BLOCK.Obsidian]: m(new THREE.MeshStandardMaterial({ color: 0x463a5c, roughness: 0.6 }), cubeGeom, 0.03),
        [BLOCK.Portal]: m(
            new THREE.MeshStandardMaterial({
                color: 0x9a5fd0,
                emissive: 0xa050ff,
                emissiveIntensity: 0.8,
                transparent: true,
                opacity: 0.75,
            }),
            cubeGeom,
            0,
        ),
        // Structure-only blocks: natural tones nudged off the terrain palette so
        // player-built shapes (cabins, stilt huts, igloos) read as crafted
        // without looking out of place in their biome.
        [BLOCK.Planks]: m(new THREE.MeshStandardMaterial({ color: 0xb98a52, roughness: 1 }), cubeGeom, 0.04),
        [BLOCK.DarkPlanks]: m(new THREE.MeshStandardMaterial({ color: 0x4a2f1a, roughness: 1 }), cubeGeom, 0.04),
        [BLOCK.Thatch]: m(new THREE.MeshStandardMaterial({ color: 0xc89556, roughness: 1 }), cubeGeom, 0.05),
        [BLOCK.MossBlock]: m(new THREE.MeshStandardMaterial({ color: 0x4a6b34, roughness: 1 }), cubeGeom, 0.06),
        // Distinct from "log" (tree trunks): warmer reddish-brown reads as
        // hewn timber against the cooler bark of natural trees.
        [BLOCK.StructureLog]: m(new THREE.MeshStandardMaterial({ color: 0x8b4a2a, roughness: 1 }), cubeGeom, 0.04),
        // Soft icy blue: separates igloo shell from surrounding snow_block
        // without breaking the snowy palette.
        [BLOCK.StructureSnow]: m(new THREE.MeshStandardMaterial({ color: 0x9ec5d9, roughness: 0.6 }), cubeGeom, 0.04),
    };

    const geometries: THREE.BufferGeometry[] = Array.from(
        new Set(Object.values(map).map((e) => e.geometry)),
    );

    return { map, geometries };
}
